import { useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@@/contexts/AuthContext';
import { PageHeader } from '@@/ui/components';
import { CampaignType } from '../types';
import { WizardStepper, WizardStep } from '../components/create/wizard-stepper';
import { SelectTypeStep } from '../components/create/select-type-step';
import { ScheduleStep, INITIAL_SCHEDULE_DATA, type ScheduleData } from '../components/create/schedule-step';
import {
  ContactListStep,
  INITIAL_CONTACT_LIST_DATA,
  type ContactListData
} from '../components/create/contact-list-step';
import { ReviewStep } from '../components/create/review-step';
import { WizardNavigation } from '../components/create/wizard-navigation';

function CreatePageContent() {
  const navigate = useNavigate();
  const { getPathWithInstanceAlias } = useContext(AuthContext);
  const [currentStep, setCurrentStep] = useState(WizardStep.TYPE);
  const [selectedType, setSelectedType] = useState<CampaignType | null>(null);
  const [scheduleData, setScheduleData] = useState<ScheduleData>(INITIAL_SCHEDULE_DATA);
  const [contactListData, setContactListData] = useState<ContactListData>(INITIAL_CONTACT_LIST_DATA);

  const handleBack = useCallback(() => {
    if (currentStep === WizardStep.TYPE) {
      navigate(getPathWithInstanceAlias('/portal/campaigns'));
    } else {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep, navigate, getPathWithInstanceAlias]);

  const handleNext = useCallback(() => {
    if (currentStep < WizardStep.REVIEW) {
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep]);

  // const handleCancel = useCallback(() => {
  //   navigate(getPathWithInstanceAlias('/portal/campaigns'));
  // }, [navigate, getPathWithInstanceAlias]);

  const canProceed = currentStep === WizardStep.TYPE ? selectedType !== null : currentStep !== WizardStep.REVIEW;

  const renderStepContent = () => {
    switch (currentStep) {
      case WizardStep.TYPE:
        return <SelectTypeStep selectedType={selectedType} onSelectType={setSelectedType} />;
      case WizardStep.MESSAGE:
        return <div className='text-muted-foreground'>Message step - coming soon</div>;
      case WizardStep.SCHEDULE:
        return <ScheduleStep data={scheduleData} onChange={setScheduleData} />;
      case WizardStep.CONTACT_LIST:
        return <ContactListStep data={contactListData} onChange={setContactListData} />;
      case WizardStep.REVIEW:
        return (
          <ReviewStep
            campaignName='Service Satisfaction Survey'
            campaignType={selectedType}
            scheduleData={scheduleData}
            contactListData={contactListData}
            onEditStep={setCurrentStep}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <PageHeader title='Create Campaign' description='Create your communication campaigns' />
      <div className='flex-1 px-10 py-6 bg-muted'>
        <div className='flex gap-6'>
          <WizardStepper currentStep={currentStep} />
          <div
            className='flex-1 bg-card rounded-xl border
              border-border p-6'
          >
            <div className='flex items-start justify-between mb-6'>
              <div className='flex-1'>{renderStepContent()}</div>
              {/* <Button variant='outline' size='sm' onClick={handleCancel} className='ml-4'>
                <X className='w-4 h-4' />
                Cancel
              </Button> */}
            </div>
            <WizardNavigation
              currentStep={currentStep}
              canProceed={canProceed}
              onBack={handleBack}
              onNext={handleNext}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export const CreatePage = CreatePageContent;
